/** Choosing a client, with a search box.
 *
 *  ## Why this exists rather than a fourth `<select>`
 *
 *  Four screens asked "which company", and one of them - the entry grid - had grown a
 *  real picker: a filter box, an All-that-is-shown checkbox, and each company's
 *  default approver beside its name. The other three were still native `<select>`s, so
 *  a firm with forty clients scrolled a list on the Add-task drawer while the
 *  "Who approves it" field directly beneath it could be typed at. That was the
 *  complaint, and writing the picker a fourth time would have been the wrong answer to
 *  it.
 *
 *  Shaped as `CompanyPicker` + `CompanyCell` deliberately, because that is exactly how
 *  `PeoplePicker` + `PersonCell` is already shaped: the grid wants a cell with a
 *  popover over it, and the drawer and the edit form want the picker with no cell
 *  around it. Two different popover patterns in one app would be two things to learn.
 *
 *  ## One component for one company and for several
 *
 *  It always speaks `string[]`, as `PeoplePicker` does, and `multiple` decides whether
 *  the rows are checkboxes or radios, whether the All row appears, and whether picking
 *  closes. Two components would have meant two copies of the filter box, the
 *  Enter-takes-the-only-match rule and the list rendering - the three things that must
 *  not drift apart. `multiple` defaults to false so a call site that forgets it gets
 *  the safe behaviour rather than a silent multi-select.
 *
 *  Nothing about the entry grid's fan-out lives here. The "N rows, one per company"
 *  footer, the 200-row cap and the per-company approver hint arrive as `footer` and
 *  `hint`, because they are that screen's business and a shared picker that knew about
 *  MAX_ROWS would be the same mistake in a new place.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { Popover } from './Popover'

import type { Company } from '@/api/client'
import { byName, toggleIn } from './ui'
import { toggleAll } from './Selection'

/** What the closed cell says, in two parts.
 *
 *  Two parts rather than one string for the reason `peopleLabel` gives: the name may
 *  be clipped by the cell and the count may not, and a clipped "+2" would turn three
 *  companies into one with nothing said.
 */
export function companyLabel(names: string[], placeholder = '—') {
  if (!names.length) return { name: placeholder, more: '' }
  return { name: names[0], more: names.length > 1 ? `+${names.length - 1}` : '' }
}

/** The list to offer, with an archived current value kept on it.
 *
 *  A saved task may point at a company that has since been archived. Archiving is what
 *  takes a client out of the new-task lists, so it is correctly absent from `active` -
 *  but dropping it from the *edit* list would mean opening a task, changing its
 *  priority, and silently moving it to whichever company sorted first.
 *
 *  Appended and MARKED rather than hidden, the way `PeoplePicker` marks somebody who
 *  has signed off. The row renders an `archived` tag, which says more than the old
 *  "(archived)" glued onto an option label could.
 */
export function withCurrent(active: Company[], current: string): Company[] {
  if (!current || active.some((c) => c.name === current)) return active
  return [...active, {
    company_id: `archived:${current}`, name: current, short_name: '',
    active: false, default_approver_user_id: '', sort_order: 0,
  }]
}

/** Whether the All box is on, off, or the third state that says "some".
 *
 *  Computed rather than inlined at the render because getting it wrong is a silent
 *  lie: a box reading "off" while four companies are ticked tells somebody the
 *  opposite of what ticking it will do.
 *
 *  About what is SHOWN, not what exists. Typing "Sharma" and then ticking All must
 *  give the Sharma companies, not all forty.
 */
export function headerTicks(picked: string[], shown: string[]) {
  const all = shown.length > 0 && shown.every((n) => picked.includes(n))
  return { all, some: !all && shown.some((n) => picked.includes(n)) }
}

/** Named for what the box will actually do. "All active companies" beside a filtered
 *  list of six would be a label describing the wrong forty. */
export function allLabel(query: string, shownCount: number): string {
  return query.trim() ? `All ${shownCount} matching` : 'All active companies'
}

export function CompanyPicker({ companies, value, onChange, onDone, multiple = false,
                                hint, footer, autoFocus = false, initialQuery }: {
  companies: Company[]
  value: string[]
  onChange: (names: string[]) => void
  onDone: () => void
  /** Several companies at once. Off by default - see the note at the top. */
  multiple?: boolean
  /** The query to start with. Set when the picker was opened by somebody typing a
   *  letter on the cell: without it that first character is swallowed, and the
   *  spreadsheet gesture - land on the cell, start typing the client - does not work.
   */
  initialQuery?: string
  /** Take the caret on mount. For the popover, where the path is open-type-Enter and
   *  there is nothing else on screen to type into.
   *
   *  Off by default, because inline in a form it is wrong twice over: the drawer
   *  already autofocuses the field somebody is meant to start at, and an autofocused
   *  input part-way down a scrolling panel drags the panel to itself on open. */
  autoFocus?: boolean
  /** Right-aligned text per row. The entry grid names the company's default
   *  approver; everything else passes nothing. */
  hint?: (c: Company) => string
  /** Replaces the whole footer bar. The entry grid puts its fan-out count and its
   *  row cap here. */
  footer?: ReactNode
}) {
  const [find, setFind] = useState(initialQuery ?? '')
  /** Which row in the list the keyboard is on. This is a combobox: while it is open
   *  the arrow keys belong to the LIST, not to whatever is behind it. -1 means the
   *  keyboard has not entered the list yet, so the first Down lands on the first
   *  option rather than the second. */
  const [at, setAt] = useState(-1)
  const list = useRef<HTMLDivElement>(null)
  const box = useRef<HTMLInputElement>(null)
  const id = useId()

  const shown = useMemo(
    () => byName(companies, find, (c) => c.name), [companies, find])
  const shownNames = shown.map((c) => c.name)
  const ticks = headerTicks(value, shownNames)

  // Typing narrows the list, so the old position means nothing - and leaving it
  // where it was would tick a company nobody was looking at.
  useEffect(() => { setAt(shown.length ? 0 : -1) }, [find, shown.length])

  // Follow the keyboard when the list is longer than its 15rem of scroll.
  useEffect(() => {
    if (at < 0) return
    list.current?.querySelectorAll('[data-option]')[at]
      ?.scrollIntoView({ block: 'nearest' })
  }, [at])

  /** Picking one. In single mode this REPLACES and closes - a single-select popover
   *  left open has nothing further to do, and a second tick that silently unticked
   *  the first would read as a bug. */
  const pick = (name: string) => {
    if (!multiple) { onChange([name]); onDone(); return }
    onChange(toggleIn(value, name))
    // Back to the search box after a CLICK, or focus sits on the checkbox and the
    // arrow keys quietly stop working for the rest of the visit.
    box.current?.focus()
  }

  return (
    <div>
      {/* Type-to-filter, replacing the type-to-find a native select gave for free.
          Autofocused, so the keyboard path is: open, type, Enter. */}
      <div className="px-2 pt-2">
        <input ref={box} className="field text-[13px]" autoFocus={autoFocus}
               value={find}
               // A listbox driven by the arrows needs to SAY so, or taking the option
               // checkboxes out of the tab order below is a trap rather than a
               // simplification: `aria-activedescendant` is what announces the row
               // the arrows are on when focus never leaves this box.
               role="combobox" aria-expanded aria-controls={`${id}-list`}
               aria-activedescendant={at >= 0 && shown[at]
                 ? `${id}-opt-${at}` : undefined}
               placeholder="Find a client…"
               onChange={(e) => setFind(e.target.value)}
               onKeyDown={(e) => {
                 // Up and down walk the list. They are the reason this is a combobox
                 // and not a text box with names under it: the whole list is
                 // reachable without touching the mouse, and without the arrows
                 // being stolen by whatever is behind the panel.
                 if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                   e.preventDefault()
                   if (!shown.length) return
                   const step = e.key === 'ArrowDown' ? 1 : -1
                   // Clamped, not wrapped: running off the end and reappearing at
                   // the top loses somebody's place in a list of forty clients.
                   setAt((n) => Math.max(0, Math.min(shown.length - 1, n + step)))
                   return
                 }
                 // Space ticks the highlighted row - but ONLY while the box is
                 // empty. A client is called "Sharma Textiles Pvt Ltd", so once
                 // somebody is typing a query the space bar has to be a space or
                 // the second word could never be reached.
                 //
                 // The two keys divide the way the work does: arrow down the list
                 // and press space, or type three letters and press Enter.
                 //
                 // Read from the INPUT, not from `find`. The state lags a render
                 // behind the keystrokes, so typing "ca r" quickly reached this
                 // with `find` still '' - and the space silently ticked somebody
                 // instead of separating the two words. The element's own value is
                 // the only thing that is true at keydown.
                 if (e.key === ' ' && !e.currentTarget.value.trim()) {
                   if (at < 0 || !shown[at]) return
                   e.preventDefault()
                   pick(shown[at].name)
                   return
                 }
                 if (e.key === 'Enter') {
                   e.preventDefault()
                   // Whatever the keyboard is on. Falling back to the only remaining
                   // match keeps the old type-three-letters-and-Enter path working
                   // for somebody who never touches the arrows.
                   const one = at >= 0 ? shown[at]
                     : shown.length === 1 ? shown[0] : null
                   if (one) { pick(one.name); setFind('') }
                   return
                 }
                 // The query first, then the popover. Escaping straight out of a
                 // narrowed list loses the narrowing and the choice in one keypress.
                 if (e.key === 'Escape') {
                   e.preventDefault()
                   if (find) setFind('')
                   else onDone()
                 }
               }} />
      </div>

      {/* Only when it can mean something. With one company to choose, "all of them"
          is not a thing somebody wants. */}
      {multiple && (
        <label className="flex items-center gap-2 px-3 py-2 border-b border-rule
                          text-[12.5px] font-semibold cursor-pointer">
          <input type="checkbox" checked={ticks.all}
                 disabled={shownNames.length === 0}
                 // Partly ticked is neither on nor off, and React does not set this
                 // as an attribute - so it goes on through a ref.
                 ref={(el) => { if (el) el.indeterminate = ticks.some }}
                 onChange={() => onChange(
                   [...toggleAll(new Set(value), shownNames)])} />
          {allLabel(find, shown.length)}
          <span className="ml-auto mono text-[11px] text-ink-3">
            {value.length}/{companies.length}
          </span>
        </label>
      )}

      <div ref={list} id={`${id}-list`} role="listbox"
           className="max-h-60 overflow-auto py-1">
        {shown.length === 0 && (
          <p className="text-[12px] text-ink-3 px-3 py-2">No client matches that.</p>
        )}
        {shown.map((c, n) => (
          <label key={c.company_id} data-option
                 id={`${id}-opt-${n}`} role="option"
                 aria-selected={value.includes(c.name)}
                 // Hovering moves the keyboard's place too, so the mouse and the
                 // arrows cannot end up pointing at two different rows.
                 onMouseEnter={() => setAt(n)}
                 className={`flex items-center gap-2 px-3 py-1 text-[12.5px]
                            cursor-pointer ${n === at
                              ? 'bg-primary-tint ring-1 ring-inset ring-primary/40'
                              : ''}`}>
            {/* `tabIndex={-1}`: the arrows plus space are the keyboard's way through
                this list, so Tab is free to move between the panel's SECTIONS -
                search box, the All box, Done - instead of costing one press per
                company. Clicking still works exactly as before; `tabIndex` only
                removes an element from SEQUENTIAL navigation. */}
            <input type={multiple ? 'checkbox' : 'radio'} tabIndex={-1}
                   checked={value.includes(c.name)}
                   onChange={() => pick(c.name)} />
            <span className="truncate">{c.name}</span>
            {/* Marked, never removed - see `withCurrent`. */}
            {!c.active && (
              <span className="text-[10.5px] px-1.5 py-px rounded bg-pend-tint
                               text-pend-ink whitespace-nowrap">
                archived
              </span>
            )}
            {hint?.(c) ? (
              <span className="ml-auto text-[11px] text-ink-3 truncate">
                {hint(c)}
              </span>
            ) : null}
          </label>
        ))}
      </div>

      {/* The count and the Done button are what FINISHES a multi-selection. Picking
          one company finishes itself - `pick` closes the popover - so a single-select
          list gets no footer at all rather than a Done button with nothing to do and
          a "Nothing chosen" line under a form field. `undefined` rather than a falsy
          check, so a caller may pass `null` to mean "no footer". */}
      {footer === undefined ? (multiple && (
        <div className="px-3 py-2 border-t border-rule flex items-center gap-2">
          <span className="text-[11.5px] text-ink-3 flex-1">
            {value.length ? `${value.length} selected` : 'Nothing ticked'}
          </span>
          <button type="button" className="btn btn-sm btn-primary" onClick={onDone}>
            Done
          </button>
        </div>
      )) : footer}
    </div>
  )
}

/** One grid cell: the summary, and the picker in a popover over it.
 *
 *  Separate from `CompanyPicker` for the reason `PersonCell` is separate - the drawer
 *  and the edit form want the picker without a cell around it. The geometry is
 *  `PersonCell`'s, deliberately: two different popovers in one row would be two
 *  things to learn.
 */
export function CompanyCell({ open, onOpen, companies, value, onChange, multiple,
                              hint, footer, placeholder = '—', summary, onExit,
                              initialQuery }: {
  open: boolean
  onOpen: (v: boolean) => void
  /** Tab has run off the end of the panel - see `Popover`. Passed straight through;
   *  only the grid knows which cell this is. */
  onExit?: (dir: 'forward' | 'back') => void
  /** Seeded when the picker was opened by typing a letter on the cell. */
  initialQuery?: string
  companies: Company[]
  value: string[]
  onChange: (names: string[]) => void
  multiple?: boolean
  hint?: (c: Company) => string
  footer?: ReactNode
  placeholder?: string
  /** What the trigger says while open. The entry grid words this in terms of the
   *  rows the fan-out is about to make. */
  summary?: (names: string[]) => string
}) {
  const label = companyLabel(value, placeholder)
  const said = open && summary ? summary(value) : ''
  const trigger = useRef<HTMLButtonElement>(null)
  return (
    <div className="relative">
      <button ref={trigger} type="button"
              className="field text-left flex items-center gap-1.5"
              aria-haspopup="listbox" aria-expanded={open}
              onClick={() => onOpen(!open)}>
        <span className={value.length ? 'truncate' : 'truncate text-ink-3'}>
          {said || label.name}
        </span>
        {/* shrink-0: the name may be clipped, the count may not - a clipped "+2"
            would turn three companies into one without saying so. */}
        {!said && label.more
          ? <span className="shrink-0 text-ink-2">{label.more}</span> : null}
        <span className="ml-auto text-ink-3 text-[10px]">▾</span>
      </button>

      {/* Portalled, so the grid's own scroll box cannot clip the panel on the last
          row. Closing by clicking away APPLIES what is ticked rather than throwing it
          away - the ticks are the decision. */}
      <Popover anchor={trigger.current} open={open} onClose={() => onOpen(false)}
               onExit={onExit}>
        {/* Mounted only while open, so the filter box resets itself and a query
            typed for one row cannot still be narrowing the list when the next
            row's picker opens. */}
        <CompanyPicker companies={companies} value={value} onChange={onChange}
                       onDone={() => onOpen(false)} multiple={multiple}
                       hint={hint} footer={footer} autoFocus
                       initialQuery={initialQuery} />
      </Popover>
    </div>
  )
}
